"use client";

import Text from "@/components/animations/Text";
import Container from "@/components/layout/Container";
import Flex from "@/components/layout/Flex";
import BackgroundImage from "@/components/media/BackgroundImage";
import Headline from "@/components/project/Headline";
import HeaderSpacer from "@/components/shared/HeaderSpacer";
import Typography from "@/components/shared/Typography";
import Visibility from "@/components/shared/Visibility";
import Tags from "@/components/ui/Tags";
import { cls, cn } from "@/lib/utils";
import HeroBreadcrumbs from "../HeroBreadcrumbs";
import type { HeroProps } from "./types";

function Hero({
  publicId,
  alt,
  className = "",
  header = "",
  headerClassName = "",
  headline = "",
  headlineClassName = "",
  highlight = "",
  highlightClassName = "",
  description = "",
  descriptionClassName = "",
  gradientClassName = "",
  fullScreen = true,
  showGradient = true,
  showImage = true,
  showBreadcrumb = true,
  showTags = true,
  useHeaderSpacing = false,
  blurBackground = false,
  tags = [],
  children,
}: HeroProps) {
  return (
    <BackgroundImage
      as="section"
      id="Hero Section"
      src={publicId}
      alt={alt}
      fill={true}
      sizes="100vw"
      fetchPriority="high"
      priority={true}
      quality={90}
      // presetRatio is a REQUIRED prop on BackgroundImage's type chain, so it
      // cannot simply be dropped. It is consumed (BackgroundImage spreads it
      // through `native` into ImageWithSkeleton, which feeds <AspectRatio> and
      // CldImage's aspectRatio) but it is inert HERE, for two measured reasons:
      //   - the AspectRatio wrapper also carries `w-full h-full` inside a
      //     full-screen ZStack, so the box is viewport-sized and the declared
      //     aspect-ratio never applies (measured in production: 375x900 and
      //     768x900, not 4/3 or 16/9);
      //   - the hero image is requested with `c_limit`, and Cloudinary ignores
      //     aspectRatio without a crop mode, so no ar_ transform is emitted.
      // A constant therefore renders identically to the old breakpoint-derived
      // value at every width, and lets useBreakpoints go.
      presetRatio="ultrawide"
      showImage={showImage}
      className={cn(cls`bg-card`, className)}
      imageClassName="object-cover"
      overlay={cn(
        showGradient &&
          cn(
            "bg-gradient-to-b from-black/45 from-75% to-background",
            gradientClassName,
          ),
        blurBackground && "backdrop-blur-sm",
      )}
      foreground={cn(
        "w-full flex flex-col justify-center items-center",
        "sm:py-6 sm:px-3",
        "md:py-10 md:px-8",
        "lg:py-12 lg:px-16",
        fullScreen && "h-screen",
      )}
    >
      <Visibility shouldShow={useHeaderSpacing}>
        <HeaderSpacer />
      </Visibility>

      {/* Content */}
      <Container
        asChild
        margin="mx-0"
        maxWidth="max-w-md"
        md="md:max-w-2xl"
        lg="lg:max-w-3xl"
      >
        <Flex.Column justify="center" items="start" gap="gap-6">
          <Visibility shouldShow={!!header}>
            <Typography.Small
              className={cn(cls`w-full text-left`, headerClassName)}
            >
              {header}
            </Typography.Small>
          </Visibility>

          <Visibility shouldShow={showBreadcrumb}>
            <HeroBreadcrumbs />
          </Visibility>

          <Visibility shouldShow={!!headline}>
            <Headline
              as="H1"
              className={cn(
                cls`w-full text-left text-foreground-secondary`,
                headlineClassName,
              )}
            >
              {headline}{" "}
              <Visibility shouldShow={!!highlight}>
                <Text.Highlight
                  className={cn("", highlightClassName)}
                  text={highlight}
                />
              </Visibility>
            </Headline>
          </Visibility>

          <Visibility shouldShow={showTags}>
            <Tags tags={tags} />
          </Visibility>

          <Visibility shouldShow={!!description}>
            <Typography.Description
              className={cn(cls`w-full`, descriptionClassName)}
              paragraphClassName="text-left text-foreground-secondary"
              description={description}
            />
          </Visibility>

          {children}
        </Flex.Column>
      </Container>
    </BackgroundImage>
  );
}

export default Hero;
